import { db } from '../firebase';
import { collection, addDoc, doc, getDoc, updateDoc } from 'firebase/firestore';

interface LevelSession {
  gameName: string;
  currentLevel: number;
  highestLevel: number;
  status: 'playing' | 'won' | 'lost' | 'expired';
  rescuedEmojis?: Record<string, number>; // Cumulative emoji collection across levels
  superEmojiCollection?: Record<string, number>; // Super emoji collection across levels
  createdAt: Date;
}

class LevelDbService {
  private static instance: LevelDbService;

  private constructor() {}

  static getInstance(): LevelDbService {
    if (!LevelDbService.instance) {
      LevelDbService.instance = new LevelDbService();
    }
    return LevelDbService.instance;
  }

  async createLevelSession(gameName: string): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, 'levels'), {
        gameName,
        currentLevel: 1,
        highestLevel: 1,
        status: 'playing',
        rescuedEmojis: {}, // Initialize with empty emoji collection
        superEmojiCollection: {}, // Initialize with empty super emoji collection
        createdAt: new Date(),
      });
      console.log("New level session created with ID: ", docRef.id);
      return docRef.id;
    } catch (e) {
      console.error("Error creating new level session: ", e);
      throw e;
    }
  }

  async getLevelSession(sessionId: string): Promise<LevelSession | null> {
    try {
      const docRef = doc(db, 'levels', sessionId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data() as LevelSession;
        // Ensure highestLevel is at least currentLevel for backward compatibility
        if (!data.highestLevel) {
          data.highestLevel = data.currentLevel;
        }
        console.log("Level session data:", data);
        return data;
      } else {
        console.log("No such level session document!");
        return null;
      }
    } catch (e) {
      console.error("Error getting level session: ", e);
      throw e;
    }
  }

  async updateLevelSession(sessionId: string, data: Partial<LevelSession>): Promise<void> {
    try {
      const docRef = doc(db, 'levels', sessionId);
      // If updating highestLevel, ensure it's not lower than current level
      if (data.highestLevel !== undefined && data.currentLevel !== undefined) {
        data.highestLevel = Math.max(data.highestLevel, data.currentLevel);
      }
      await updateDoc(docRef, data);
      console.log("Level session updated successfully!");
    } catch (e) {
      console.error("Error updating level session: ", e);
      throw e;
    }
  }

  async updateEmojiCollection(sessionId: string, rescuedEmojis: Record<string, number>): Promise<void> {
    try {
      const docRef = doc(db, 'levels', sessionId);
      await updateDoc(docRef, { rescuedEmojis });
      
      const totalCount = Object.values(rescuedEmojis).reduce((sum, count) => sum + count, 0);
      const emojiList = Object.entries(rescuedEmojis)
        .map(([emoji, count]) => `${emoji}×${count}`)
        .join(', ');
      console.log(`💾 Emoji collection saved to session: ${emojiList} (${totalCount} total)`);
    } catch (e) {
      console.error("Error updating emoji collection: ", e);
      throw e;
    }
  }

  async updateSuperEmojiCollection(sessionId: string, superEmojiCollection: Record<string, number>): Promise<void> {
    try {
      const docRef = doc(db, 'levels', sessionId);
      await updateDoc(docRef, { superEmojiCollection });
      
      const totalCount = Object.values(superEmojiCollection).reduce((sum, count) => sum + count, 0);
      const emojiList = Object.entries(superEmojiCollection)
        .map(([emoji, count]) => `${emoji}×${count}`)
        .join(', ');
      console.log(`🌟 Super emoji collection saved to session: ${emojiList} (${totalCount} total)`);
    } catch (e) {
      console.error("Error updating super emoji collection: ", e);
      throw e;
    }
  }
}

export const levelDbService = LevelDbService.getInstance(); 